import { adminSupabase, supabase, WHATSAPP_SERVICE_PRODUCT_ID } from '@/lib/supabase'
import type {
  BillingAdjustment,
  BillingCharge,
  BillingSnapshot,
  LineItem,
  StorageBilling,
} from '@/types/billing'

function asLineItems(raw: unknown): LineItem[] {
  if (!Array.isArray(raw)) return []
  return raw as LineItem[]
}

type BillingAction = 'get_snapshot' | 'get_charges' | 'get_adjustments' | 'get_storage' | 'compute'

async function invokeAdminBilling<T>(action: BillingAction, orgId: string): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Admin session expired')

  const { data, error } = await adminSupabase.functions.invoke<{ ok: boolean; data?: T; error?: string }>(
    'admin-billing',
    {
      body: { action, org_id: orgId },
      headers: { Authorization: `Bearer ${session.access_token}` },
    }
  )
  if (error) throw error
  if (!data?.ok) throw new Error(data?.error || `${action} failed`)
  return data.data as T
}

export const billingApi = {
  getSnapshot: async (orgId: string): Promise<BillingSnapshot | null> => {
    const data = await invokeAdminBilling<Record<string, unknown> | null>('get_snapshot', orgId)
    if (!data) return null
    return {
      ...(data as Omit<BillingSnapshot, 'line_items'>),
      line_items: asLineItems((data as { line_items?: unknown }).line_items),
    }
  },

  recomputeSnapshot: async (orgId: string): Promise<BillingSnapshot> => {
    await invokeAdminBilling<{ line_items: LineItem[]; monthly_total_agorot: number }>('compute', orgId)
    const fresh = await billingApi.getSnapshot(orgId)
    if (!fresh) {
      throw new Error('Snapshot missing after compute')
    }
    return fresh
  },

  getCharges: async (orgId: string, limit = 20): Promise<BillingCharge[]> => {
    const data = await invokeAdminBilling<BillingCharge[]>('get_charges', orgId)
    return (data ?? []).slice(0, limit).map((row) => ({
      ...(row as BillingCharge),
      line_items: asLineItems((row as { line_items?: unknown }).line_items),
    }))
  },

  listAdjustments: async (orgId: string): Promise<BillingAdjustment[]> => {
    const data = await invokeAdminBilling<BillingAdjustment[]>('get_adjustments', orgId)
    return (data ?? []) as BillingAdjustment[]
  },

  getStorageBilling: async (orgId: string): Promise<StorageBilling | null> => {
    const data = await invokeAdminBilling<StorageBilling | null>('get_storage', orgId)
    if (!data) return null
    return {
      org_id: data.org_id as string,
      free_gb: Number(data.free_gb ?? 5),
      price_per_gb_agorot: Number(data.price_per_gb_agorot ?? 0),
      used_gb: Number(data.used_gb ?? 0),
      last_measured_at: (data.last_measured_at as string | null) ?? null,
      updated_at: data.updated_at as string,
    }
  },

  listHubDevicesForOrg: async (orgId: string) => {
    const { data, error } = await adminSupabase
      .from('whatsapp_devices')
      .select('id, label, billing_status, price_agorot, provider, created_at')
      .eq('org_id', orgId)
      .order('created_at', { ascending: true })
    if (error) throw error
    return (data ?? []) as Array<{
      id: string
      label: string | null
      billing_status: string
      price_agorot: number
      provider: string | null
      created_at: string
    }>
  },

  getCentralSubscriptionStatus: async (orgId: string): Promise<{ plan: string; status: string } | null> => {
    const { data, error } = await adminSupabase
      .from('central_subscriptions')
      .select('plan, status')
      .eq('org_id', orgId)
      .eq('product_id', WHATSAPP_SERVICE_PRODUCT_ID)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    return { plan: String(data.plan ?? ''), status: String(data.status ?? '') }
  },
}
